import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { marked } from "marked";
import { ContentChange, QuillEditorComponent } from "ngx-quill";
import Quill from "quill";
import "quill-mention";
import { take } from "rxjs";
import { CommentTargetType, IComment } from "@gtmhub/comments";
import { localize } from "@gtmhub/localization";
import { SearchService } from "@gtmhub/shared/services/search.service";
import { IdMap } from "@gtmhub/util";
import { CommentsFacade } from "@webapp/comments/services/comments-facade.service";
import { ISearchParams, SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search, SearchDTO } from "@webapp/search/models/search.models";
import { IGif, SizeOfGif } from "@webapp/shared/rich-text-editor/add-gif/add-gif.models";
import { MentionBlot } from "@webapp/shared/rich-text-editor/blots/gh-mention.blot";
import { MentionDeletedEvent } from "@webapp/shared/rich-text-editor/events/mention-deleted.event";
import { deltaToMarkdownConverters } from "@webapp/shared/rich-text-editor/markdown-converter/from-delta.converters";
import { MarkedJsService } from "@webapp/shared/rich-text-editor/marked-js/marked-js.service";
import { MentionPopoverUser } from "@webapp/shared/rich-text-editor/mention-popover/mention-popover.models";
import { RichTextEditorIconEnum } from "@webapp/shared/rich-text-editor/rich-text-editor-icon/rich-text-editor-icon.enum";
import { UsersFacade } from "@webapp/users/services/users-facade.service";
import { EmbeddedEmoji } from "./blots/embedded-emoji.blot";
import { deltaToMarkdown } from "./markdown-converter/markdown-converter";
import { GhQuillTheme } from "./rich-text-editor.theme";
import { searchItemToMentionListUser, searchItemToMentionPopoverUser } from "./rich-text-editor.utils";
import { getCurrentUserId } from "@gtmhub/users";

Quill.register("themes/ghQuillTheme", GhQuillTheme, true);
Quill.register(MentionBlot);
Quill.register(EmbeddedEmoji);

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: "rich-text-editor",
  templateUrl: "./rich-text-editor.component.html",
  styleUrls: ["./rich-text-editor.component.less"],
})
/**
 * @example <rich-text-editor [onCommentPosted)="doSomething($event)"></rich-text-editor>
 * @example <rich-text-editor [(text)]="externalVariable" [hidePostButton]="true"></rich-text-editor>
 * @example <rich-text-editor [text]="externalVariable" [displayOnly]="true"></rich-text-editor>
 *
 * @param targetId string - targetId for comment
 * @param targetType CommentTargetType - targetType for comment
 * @param targetTitle string - targetTitle for comment
 * @param hidePostButton boolean (default: default) - Hides the post comment button, e.g. when used in inline mode
 * @param withoutGifs boolean (default: default) - Removes the GIF selector
 * @param inline boolean (default: default) - Hide the toolbar unless focused
 * @param readOnly boolean (default: false) - Disable editing
 * @param displayOnly boolean (default: false) - Only show the content without toolbar, editing or placeholder
 * @param clearTextAfterPost boolean (default: true) - After posting the comment, should the text field be cleared
 * @param focusOnEditable boolean (default: true) - Should field be focused when displayOnly switches from true to false, e.g. when editing comments
 * @param commentId string - If populated the post button will update the comment with the given ID instead of inserting a new comment
 * @param text string (default: "") - Text to be populated in the editor
 * @param placeholder string - Placeholder for the editor when no text is input
 * @param gif IGif - Gif to be populated in the editor
 * @param textChange EventEmitter<string>
 * @param markdownChange EventEmitter<string> - Temporary until we scrap markdown all in favor of rich text
 * @param gifChange EventEmitter<gif>
 * @param mentionsChange EventEmitter<string[]
 * @param commentPosted EventEmitter<IComment>
 * @param onBlur EventEmitter<void>
 */
export class RichTextEditorComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() public targetId: string;
  @Input() public targetType: CommentTargetType;
  @Input() public targetTitle: string;
  @Input() public hidePostButton: boolean;
  @Input() public withoutGifs: boolean;
  @Input() public inline: boolean;
  @Input() public readOnly: boolean;
  @Input() public displayOnly: boolean;
  @Input() public clearTextAfterPost = true;
  @Input() public focusOnEditable = true;
  @Input() public commentId: string;
  @Input() public text = "";
  @Input() public placeholder: string;
  @Input() public gif: IGif;
  @Input() public additionalMentions: string[] = [];

  @Output() public textChange = new EventEmitter<string>();
  @Output() public markdownChange = new EventEmitter<string>();
  @Output() public gifChange = new EventEmitter<IGif>();
  @Output() public mentionsChange = new EventEmitter<string[]>();
  @Output() public commentPosted = new EventEmitter<IComment>();
  @Output() public commentEdited = new EventEmitter<IComment>();
  @Output() public onBlur = new EventEmitter<void>();

  @ViewChild(QuillEditorComponent, { static: true }) public editor: QuillEditorComponent;
  @ViewChild("richTextEditor") public element: ElementRef;

  public modules = {};
  public sizeOfGif = SizeOfGif;
  public isFocused = false;
  public mentionPopoverUser: MentionPopoverUser;
  public mentionElRef: ElementRef;
  public mentions: string[] = [];
  public icons = RichTextEditorIconEnum;
  public richText = "";

  private users: IdMap<MentionPopoverUser> = {};
  private gifChanges = {
    isDeleted: false,
    isAdded: false,
  };
  private textCursorPositionBeforeOpeningEmojiPopup: { index: number; length: number };
  private areMentionsOpened = false;
  private removeMentionClickedListener: () => void;
  private removeMentionDeletedListener: () => void;

  private markdownText = "";

  constructor(
    private commentsFacade: CommentsFacade,
    private searchService: SearchService,
    private markedJsService: MarkedJsService,
    private usersFacade: UsersFacade,
    private renderer2: Renderer2
  ) {
    this.markedJsService.loadCustomExtensionsForRichTextEditor();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const isDisplayOnlyChangedOff = changes.displayOnly?.previousValue && !changes.displayOnly?.currentValue;

    if (this.focusOnEditable && isDisplayOnlyChangedOff) {
      // Small timeout is needed for the focus event to work
      setTimeout(() => {
        const quillContainer = this.editor?.elementRef?.nativeElement;
        const innerEditor = quillContainer?.querySelector(".ql-editor");

        innerEditor?.focus();
      }, 50);
    }

    const isTextChanged =
      (changes.text?.firstChange || changes.text?.previousValue !== changes.text?.currentValue) &&
      !!changes.text?.currentValue &&
      changes.text?.currentValue !== this.markdownText;
    if (isTextChanged) {
      this.richText = this.convertMarkdownToRichText(changes.text.currentValue);
    }
  }

  public ngAfterViewInit(): void {
    this.modules = {
      mention: {
        blotName: "ghMention",
        dataAttributes: ["id", "value", "denotationChar", "email", "avatar"],
        renderItem: (item): string => {
          return this.renderMentionItem(item);
        },
        onSelect: (item, insertItem): void => {
          insertItem(item);

          this.addMention(item.id);

          // Necessary because quill-mention triggers changes as 'api' instead of 'user'
          this.addTextToQuillEditor("");
        },
        onOpen: (): void => {
          this.areMentionsOpened = true;
        },
        onClose: (): void => {
          this.areMentionsOpened = false;
        },
        source: async (searchTerm: string, renderList): Promise<void> => {
          const searchBody: SearchDTO = {
            searchRequests: [
              {
                collectionName: "users",
                ...(searchTerm && {
                  searchFields: [{ name: "firstName" }, { name: "lastName" }, { name: "fullName" }],
                  responseFields: ["_id", "firstName", "lastName", "auth0Cache.email", "auth0Cache.picture"],
                }),
              },
            ],
            ...(searchTerm && {
              searchTerm,
              operator: SearchOperatorsEnum.matchPrefix,
            }),
          };
          const queryParams: Partial<ISearchParams> = { take: 10, skip: 0 };
          const searchResults = await this.searchService.getSomeSearchData<Search<"users">>(searchBody, queryParams);

          const renderListValues = [];
          searchResults.items.forEach((item) => {
            this.users[item.id] = searchItemToMentionPopoverUser(item);
            renderListValues.push(searchItemToMentionListUser(item));
          });

          renderList(renderListValues, searchTerm);
        },
      },
    };

    this.removeMentionClickedListener = this.renderer2.listen(this.element.nativeElement, "mention-clicked", (e: CustomEvent) => {
      this.mentionElRef = new ElementRef(e.detail.domNode);
      this.getUserPopoverInfo(e.detail.value.id);
    });

    this.removeMentionDeletedListener = this.renderer2.listen(this.element.nativeElement, "mention-deleted", (e: MentionDeletedEvent) => {
      this.removeMention(e.userId);
    });
  }

  public ngOnDestroy(): void {
    this.removeMentionClickedListener?.();
    this.removeMentionDeletedListener?.();
  }

  public getPlaceholder(): string {
    if (this.displayOnly) {
      return "";
    }

    return this.placeholder || localize("message");
  }

  public async postComment(): Promise<void> {
    if (!this.canPostComment()) {
      return;
    }

    const allMentions = [...new Set(this.mentions.concat(this.additionalMentions))];
    if (this.commentId) {
      this.commentsFacade
        .edit$(
          this.commentId,
          {
            text: this.markdownText || "",
            mentioned: allMentions,
            gif: this.gif ?? { id: "", searchQuery: "" }, // TODO the gifChanges seem to be completely ignored and the reason GIFs are "delete" is because they're replaced with "blanks"
          },
          this.gifChanges
        )
        .pipe(take(1))
        .subscribe((res) => {
          this.commentEdited.emit(res);
        });
    } else {
      this.commentsFacade
        .postComment$({
          targetId: this.targetId,
          targetType: this.targetType,
          targetTitle: this.targetTitle,
          text: this.markdownText,
          mentioned: allMentions,
          gif: this.gif,
        })
        .pipe(take(1))
        .subscribe((res) => {
          this.commentPosted.emit(res);
        });
    }

    if (this.clearTextAfterPost) {
      this.richText = "";
      this.markdownText = "";
      this.gif = null;
    }
  }

  public canPostComment(): boolean {
    return !!this.markdownText || !!this.gif;
  }

  public selectGif(gif: IGif): void {
    this.gif = gif;

    if (this.commentId) {
      this.gifChanges = {
        isAdded: true,
        isDeleted: false,
      };
    }

    this.gifChange.emit(gif);
  }

  public removeGif(): void {
    this.gif = null;

    if (this.commentId) {
      this.gifChanges = {
        isAdded: false,
        isDeleted: true,
      };
    }

    this.gifChange.emit(null);
  }

  public addEmoji(emoji: string): void {
    this.addEmojiToQuillEditor(emoji);
  }

  public showMentionMenu(): void {
    if (!this.editor) {
      return;
    }

    const { quillEditor } = this.editor;
    const mention = quillEditor.getModule("mention");
    mention.openMenu("@");
  }

  public popupMenuOpen(): void {
    if (!this.editor) {
      return;
    }

    const { quillEditor } = this.editor;
    this.textCursorPositionBeforeOpeningEmojiPopup = quillEditor.getSelection();

    // Fallback if currently there is no selection.
    // This is needed for example when editor is not focused and emoji picker is opened.
    // In this case if there is no selection and you select an emoji, it will not be inserted to the editor.
    if (!this.textCursorPositionBeforeOpeningEmojiPopup) {
      quillEditor.setSelection(quillEditor.getLength(), 0);
      this.textCursorPositionBeforeOpeningEmojiPopup = quillEditor.getSelection();
    }

    if (this.areMentionsOpened) {
      quillEditor.deleteText(this.textCursorPositionBeforeOpeningEmojiPopup.index - 1, 1, Quill.sources.USER);
    }
  }

  public popupMenuClose(event: MouseEvent): void {
    if (!event?.composedPath().includes(this.element.nativeElement)) {
      this.blurHandler();
    }
  }

  public focusHandler(): void {
    if (!this.readOnly) {
      this.isFocused = true;
    }
  }

  public blurHandler(): void {
    if (!this.readOnly) {
      this.isFocused = false;
      this.onBlur.emit();
    }
  }

  public mentionPopoverClosed(): void {
    this.mentionPopoverUser = null;
    this.mentionElRef = null;
  }

  public onContentChanged(event: ContentChange): void {
    const markdown = deltaToMarkdown(event.content.ops, deltaToMarkdownConverters()).trim();
    this.markdownText = markdown;

    this.textChange.emit(markdown);
  }

  private convertMarkdownToRichText(text: string): string {
    const trimmedTextWithReplacedNewLines = text.trim().replace(/^\n/gm, "\n<br>\n\n");

    return marked
      .parse(trimmedTextWithReplacedNewLines, {
        breaks: true,
      })
      .trim();
  }

  private addTextToQuillEditor(text: string): void {
    if (!this.editor) {
      return;
    }

    const { quillEditor } = this.editor;
    const idx = quillEditor.getLength() - 1;

    quillEditor.insertText(idx, text, Quill.sources.USER);
  }

  private addEmojiToQuillEditor(text: string): void {
    if (!this.editor || !this.textCursorPositionBeforeOpeningEmojiPopup) {
      return;
    }

    const { quillEditor } = this.editor;

    if (this.textCursorPositionBeforeOpeningEmojiPopup.length > 0) {
      quillEditor.deleteText(this.textCursorPositionBeforeOpeningEmojiPopup.index, this.textCursorPositionBeforeOpeningEmojiPopup.length);
    }

    quillEditor.insertEmbed(this.textCursorPositionBeforeOpeningEmojiPopup.index, EmbeddedEmoji.blotName, text.trim(), Quill.sources.USER);
    quillEditor.setSelection(this.textCursorPositionBeforeOpeningEmojiPopup.index + 1, 0, Quill.sources.USER);
  }

  private addMention(userId: string): void {
    if (userId === getCurrentUserId()) {
      return;
    }

    this.mentions.push(userId);
    this.mentions = [...new Set(this.mentions)];

    this.mentionsChange.emit(this.mentions);
  }

  private removeMention(userId: string): void {
    this.mentions = this.mentions.filter((it) => it !== userId);

    this.mentionsChange.emit(this.mentions);
  }

  /**
   * Render mention item
   */
  private renderMentionItem(item): string {
    const picture = item.avatar ? item.avatar : "/dist/img/icons/unknown.svg";
    return "<span class='mention-item-avatar'><img src='" + picture + "'/></span>" + "<span class='mention-item-content'>" + item.value + "</span>";
  }

  private getUserPopoverInfo(targetUserId: string): void {
    if (this.users[targetUserId]) {
      this.mentionPopoverUser = { ...this.users[targetUserId], email: this.users[targetUserId].email };
      return;
    }

    const filter = { filter: { _id: targetUserId }, fields: ["id", "firstName", "lastName", "email", "picture"] };
    this.usersFacade
      .getUsersV2$(filter)
      .pipe(take(1))
      .subscribe((payload) => {
        const mentionedUser = payload.items.length ? payload.items[0] : null;
        if (!mentionedUser) {
          return;
        }

        this.users[mentionedUser.id] = mentionedUser;
        this.mentionPopoverUser = { ...this.users[targetUserId], email: this.users[targetUserId].email };
      });
  }
}
